/**
 * Relationship-tier breakdown donut (Power / Steady / At-risk / Dormant).
 * Used on both the Overview and Relationship tabs.
 */
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { RELATIONSHIP_TIER_COLORS, RELATIONSHIP_TIER_LABEL } from '../theme';
import { formatInt, formatPercent } from '../format';

export interface TierSlice {
  tier: string;
  count: number;
}

export interface RelationshipTierDonutProps {
  data: TierSlice[];
  height?: number;
}

const TIER_ORDER = ['power', 'steady', 'at_risk', 'dormant'];

export const RelationshipTierDonut: React.FC<RelationshipTierDonutProps> = ({
  data,
  height = 240,
}) => {
  const total = data.reduce((sum, s) => sum + s.count, 0);
  const sorted = [...data].sort(
    (a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier),
  );

  if (total === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-gray-500">
        No users scored yet.
      </div>
    );
  }

  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie
            data={sorted}
            dataKey="count"
            nameKey="tier"
            cx="50%"
            cy="50%"
            innerRadius="58%"
            outerRadius="88%"
            paddingAngle={2}
            isAnimationActive={false}
          >
            {sorted.map((s) => (
              <Cell key={s.tier} fill={RELATIONSHIP_TIER_COLORS[s.tier] ?? 'var(--text-tertiary)'} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              fontSize: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
            }}
            formatter={(value, name) => [
              `${formatInt(Number(value))} (${formatPercent((Number(value) / total) * 100)})`,
              RELATIONSHIP_TIER_LABEL[String(name)] ?? name,
            ]}
          />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-gray-900 tabular-nums">{formatInt(total)}</span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Scored users
        </span>
      </div>
      <ul className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
        {sorted.map((s) => (
          <li key={s.tier} className="flex items-center gap-2">
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: RELATIONSHIP_TIER_COLORS[s.tier] ?? 'var(--text-tertiary)' }}
            />
            <span className="text-gray-600">{RELATIONSHIP_TIER_LABEL[s.tier] ?? s.tier}</span>
            <span className="ml-auto font-semibold tabular-nums text-gray-800">
              {formatInt(s.count)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
